import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';

export type UserRole =
  | 'COMMANDER'
  | 'OPERATOR'
  | 'ANALYST'
  | 'VIEWER'
  | 'UNIT_COMMANDER'
  | 'UNIT_OPERATOR'
  | 'FIELD_OPERATOR';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  unitId: string | null;
  platoonId: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  login: (email: string, password: string, selectedUnitId?: string | null) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshTokens: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      login: async (email: string, password: string, selectedUnitId?: string | null) => {
        set({ isLoading: true, error: null });
        try {
          // Pass unitId to login endpoint (for Brigade HQ users only)
          // Unit users will have their unitId enforced by backend
          const response = await api.post('/auth/login', { 
            email: email, 
            password,
          });
          const { user, accessToken, refreshToken } = response.data;
          
          // Backend returns the effective unitId (user's own for unit users, or requested for HQ)
          set({
            user: {
              ...user,
              unitId: user.unitId, // Use what backend returns (already enforced)
            },
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          
          // Set token for API calls
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          
          // Initialize unit store
          const { useUnitStore } = await import('./unitStore');
          const unitStore = useUnitStore.getState();
          await unitStore.fetchUnits();
          
          if (user.unitId === null) {
            // Brigade HQ: use selected unit or null (all)
            unitStore.setSelectedUnit(selectedUnitId || null);
          } else {
            // Unit users: usually locked to their unit
            // Exception: 86ISB acts as Brigade HQ (all units view)
            const b = unitStore.units.find(x => x.id === user.unitId);
            if (b?.role === 'HQ') {
              unitStore.setSelectedUnit(selectedUnitId || null); // default = all
            } else {
              unitStore.setSelectedUnit(user.unitId);
            }
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || 'Login failed',
          });
          throw error;
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          await api.post('/auth/logout', { refreshToken });
        } catch {
          // Ignore logout errors
        }
        
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
        
        delete api.defaults.headers.common['Authorization'];
      },

      checkAuth: async () => {
        const { accessToken, refreshTokens, user } = get();
        
        // If we have a persisted user and token, set authenticated immediately
        // This prevents redirect to login on page reload
        if (accessToken && user) {
          set({ 
            isAuthenticated: true, 
            isLoading: false 
          });
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          
          // Verify token in background (non-blocking)
          try {
            const response = await api.get('/auth/me');
            set({
              user: response.data.user,
              isAuthenticated: true,
            });

            // Ensure unit selection logic is applied on reload
            try {
              const { useUnitStore } = await import('./unitStore');
              const unitStore = useUnitStore.getState();
              if (!unitStore.units || unitStore.units.length === 0) {
                await unitStore.fetchUnits();
              }
              const u = response.data.user as User;
              if (u?.unitId) {
                const b = unitStore.units.find(x => x.id === u.unitId);
                if (b?.role === 'HQ') {
                  unitStore.setSelectedUnit(null); // all units
                } else {
                  unitStore.setSelectedUnit(u.unitId); // lock
                }
              }
            } catch {
              // ignore unit store init issues; auth is still valid
            }
          } catch (error: any) {
            if (error.response?.status === 401) {
              // Token expired, try refresh
              try {
                await refreshTokens();
                const response = await api.get('/auth/me');
                set({
                  user: response.data.user,
                  isAuthenticated: true,
                });

                // Ensure unit selection logic is applied after refresh as well
                try {
                  const { useUnitStore } = await import('./unitStore');
                  const unitStore = useUnitStore.getState();
                  if (!unitStore.units || unitStore.units.length === 0) {
                    await unitStore.fetchUnits();
                  }
                  const u = response.data.user as User;
                  if (u?.unitId) {
                    const b = unitStore.units.find(x => x.id === u.unitId);
                    if (b?.role === 'HQ') {
                      unitStore.setSelectedUnit(null);
                    } else {
                      unitStore.setSelectedUnit(u.unitId);
                    }
                  }
                } catch {
                  // ignore
                }
              } catch {
                // Refresh failed, clear auth
                set({
                  user: null,
                  accessToken: null,
                  refreshToken: null,
                  isAuthenticated: false,
                });
              }
            }
          }
          return;
        }
        
        if (!accessToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }
        
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        try {
          const response = await api.get('/auth/me');
          set({
            user: response.data.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          if (error.response?.status === 401) {
            // Try to refresh token
            try {
              await refreshTokens();
              // Retry auth check
              const response = await api.get('/auth/me');
              set({
                user: response.data.user,
                isAuthenticated: true,
                isLoading: false,
              });
            } catch {
              set({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
              });
            }
          } else {
            set({ isLoading: false, isAuthenticated: false });
          }
        }
      },

      refreshTokens: async () => {
        const { refreshToken } = get();
        
        if (!refreshToken) {
          throw new Error('No refresh token');
        }
        
        try {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

          // Only replace the refresh token when the server returned a new one; otherwise
          // keep the existing token so a refresh response that omits it can't wipe it.
          set({
            accessToken: newAccessToken,
            ...(newRefreshToken ? { refreshToken: newRefreshToken } : {}),
          });
          
          api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        } catch (error) {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
          throw error;
        }
      },
    }),
    {
      name: 'opmonitor-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user, // Persist user to prevent login redirect on reload
      }),
    }
  )
);

